/**
 * Composition root.
 *
 * ## Gate order
 *
 * `LockProvider` sits OUTSIDE the data providers, and the locked and hidden
 * states short-circuit before either of them mounts. That ordering is the point:
 * a locked app must not read entries, must not derive statistics, and must not
 * hold plaintext in memory. Rendering a data provider "but hiding the output"
 * would satisfy the visual requirement and miss the actual one.
 *
 * ## Why the selected day lives here
 *
 * The calendar opens the day sheet, but `no-feature-cycles` forbids one feature
 * importing another — two features that know about each other are one feature
 * with extra steps, and the community feature has to stay deletable in a single
 * commit (roadmap Section 5). So the state connecting them lives here.
 *
 * The day sheet sits outside the router so it survives a tab change rather than
 * unmounting mid-edit, which would fire NoteField's flush from navigation.
 */
import { useState } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import type { ISODate } from '@domain/types'
import {
  EntryProvider,
  LockProvider,
  SettingsProvider,
  SyncProvider,
  useLock,
} from '@contexts/index'
import { CalendarPage } from '@features/calendar'
import { DataPage } from '@features/data'
import { DaySheet } from '@features/entry'
import { InsightsPage } from '@features/stats'
import { TodayPage } from '@features/today'
import { ErrorBoundary } from './ErrorBoundary'
import { Layout } from './Layout'
import { LockScreen } from './LockScreen'
import { NeutralScreen } from './NeutralScreen'
import { IntroScreen } from './IntroScreen'
import { Analytics } from './Analytics'

export function App() {
  const [showIntro, setShowIntro] = useState(true)

  return (
    <ErrorBoundary>
      <LockProvider>
        {showIntro && <IntroScreen onComplete={() => setShowIntro(false)} />}
        <Gate />
      </LockProvider>
    </ErrorBoundary>
  )
}


function Gate() {
  const { phase, hidden } = useLock()

  // Quick exit takes precedence over everything, including the lock screen: the
  // point is that the visible content changes in one action (FR-113).
  if (hidden) return <NeutralScreen />

  if (phase === 'loading') return null
  if (phase === 'locked') return <LockScreen />

  return <UnlockedApp />
}

function UnlockedApp() {
  const [openDay, setOpenDay] = useState<ISODate>()

  return (
    <SettingsProvider>
      <EntryProvider>
        {/* Inside EntryProvider: sync writes entries as records arrive, and the
            entry provider owns the in-memory view those writes have to reach.
            Inside the lock gate too, so a locked app never signs in — the
            identity is sealed under the data key, which makes that automatic
            rather than a rule someone has to remember. */}
        <SyncProvider>
        <BrowserRouter>
          <Analytics />
          <Routes>
            <Route element={<Layout />}>
              <Route index element={<CalendarPage onOpenDay={setOpenDay} />} />
              <Route path="today" element={<TodayPage />} />
              <Route path="insights" element={<InsightsPage />} />
              <Route path="data" element={<DataPage />} />
              {/* Unknown paths fall back to the calendar rather than a 404:
                  a health app should never show someone a dead end. */}
              <Route path="*" element={<CalendarPage onOpenDay={setOpenDay} />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <DaySheet date={openDay} onClose={() => setOpenDay(undefined)} />
        </SyncProvider>
      </EntryProvider>
    </SettingsProvider>
  )
}
